import type { CashPlan, CashPlanOccurrence, CashPlans } from "./cash-plan-contract.js";
import type { WorkbookDashboard, WorkbookSheet } from "./finance-workbook-contract.js";

export const cashPlanLabels: Record<CashPlan["category"], string> = {
  maas: "Maaş ve personel", sgk: "SGK", vergi: "Vergi", kira: "Kira", enerji: "Enerji", diger: "Diğer planlar",
};
export function cashPlanDates(plan: CashPlan, end: string): string[] {
  const dates: string[] = [], start = new Date(`${plan.startDate}T00:00:00Z`);
  for (let index = 0; index < 1000; index++) {
    const date = new Date(start);
    if (plan.frequency === "weekly") date.setUTCDate(start.getUTCDate() + index * 7);
    if (plan.frequency === "monthly") {
      const month = start.getUTCMonth() + index;
      date.setUTCDate(1); date.setUTCMonth(month);
      date.setUTCDate(Math.min(start.getUTCDate(), new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()));
    }
    const value = date.toISOString().slice(0, 10);
    if (value > end || (plan.endDate && value > plan.endDate)) return dates;
    dates.push(value);
    if (plan.frequency === "once") return dates;
  }
  throw new Error("Tekrarlayan plan sınırı aşıldı. Başlangıç/bitiş tarihini daraltın.");
}
export function cashPlanOccurrence(plan: CashPlan, date: string): CashPlanOccurrence {
  const revision = [...(plan.revisions ?? [])].sort((a, b) => a.fromDate.localeCompare(b.fromDate))
    .filter(item => item.fromDate <= date).at(-1);
  const override = plan.occurrences?.find(item => item.date === date);
  const value = override ?? revision ?? plan;
  return { date, dueDate: override?.dueDate ?? date, name: value.name, category: value.category,
    amount: value.amount, currency: value.currency, direction: value.direction,
    cancelled: !plan.active || Boolean(override?.cancelled) || Boolean(plan.archivedFrom && date >= plan.archivedFrom) };
}
export function cashPlanOccurrences(plan: CashPlan, end: string, from?: string): CashPlanOccurrence[] {
  const dates = new Set(cashPlanDates(plan, end));
  // Moving a later occurrence into the visible period must retain its original identity.
  for (const item of plan.occurrences ?? []) if (item.dueDate <= end) dates.add(item.date);
  return [...dates].map(date => cashPlanOccurrence(plan, date))
    .filter(item => item.dueDate <= end && (!from || item.dueDate >= from))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.date.localeCompare(b.date));
}
export function cashPlanOccurrenceAmounts(data: CashPlans, plan: CashPlan, date: string) {
  const item = cashPlanOccurrence(plan, date);
  const bound = data.bindings.filter(binding => binding.planId === plan.id && binding.date === date)
    .reduce((sum, binding) => sum + BigInt(binding.amount), 0n);
  return { item, planned: BigInt(item.amount), bound, remaining: item.cancelled ? 0n : BigInt(item.amount) - bound };
}
export function validateCashPlans(data: CashPlans, book?: WorkbookDashboard, previous?: CashPlans) {
  if (new Set(data.plans.map(plan => plan.id)).size !== data.plans.length) throw new Error("Tekrarlı plan kimliği.");
  if (new Set((data.cashAvailability ?? []).map(item => `${item.date}:${item.currency}`)).size !== (data.cashAvailability ?? []).length)
    throw new Error("Aynı tarih ve döviz için yalnız bir kısıtlı nakit beyanı olabilir.");
  for (const oldPlan of previous?.plans ?? []) if (!data.plans.some(plan => plan.id === oldPlan.id))
    throw new Error("Plan geçmişi silinemez. Planı seçtiğiniz tarihten itibaren arşivleyin.");
  for (const plan of data.plans) {
    if (new Set((plan.revisions ?? []).map(item => item.fromDate)).size !== (plan.revisions ?? []).length)
      throw new Error("Aynı tarihte iki plan revizyonu olamaz.");
    if (new Set((plan.occurrences ?? []).map(item => item.date)).size !== (plan.occurrences ?? []).length)
      throw new Error("Aynı plan dönemi iki kez düzenlenemez.");
    for (const item of [...(plan.revisions ?? []).map(value => value.fromDate), ...(plan.occurrences ?? []).map(value => value.date)])
      if (!cashPlanDates(plan, item).includes(item)) throw new Error("Düzenlenen plan dönemi takvimde bulunamadı.");
  }
  const totals = new Map<string, bigint>(), sourceTotals = new Map<string, bigint>();
  const seen = new Set<string>(), changedSources = new Set<string>();
  for (const binding of data.bindings) {
    const plan = data.plans.find(item => item.id === binding.planId);
    if (!plan || !cashPlanDates(plan, binding.date).includes(binding.date)) throw new Error("Plan dönemi eşleşmiyor.");
    const occurrence = cashPlanOccurrence(plan, binding.date);
    const key = `${plan.id}:${binding.date}`, sourceKey = `${binding.sourceSheet}:${binding.sourceRow}`;
    const identity = `${key}:${sourceKey}`;
    if (seen.has(identity)) throw new Error("Aynı plan dönemi ve belge iki kez bağlanamaz.");
    seen.add(identity);
    totals.set(key, (totals.get(key) ?? 0n) + BigInt(binding.amount));
    if ((totals.get(key) ?? 0n) > BigInt(occurrence.amount)) throw new Error("Bağlanan tutar plan dönemi tutarını aşıyor. Önce belge eşlemesini inceleyin.");
    sourceTotals.set(sourceKey, (sourceTotals.get(sourceKey) ?? 0n) + BigInt(binding.amount));
    const unchanged = previous?.bindings.some(item => JSON.stringify(item) === JSON.stringify(binding));
    if (unchanged) {
      const oldPlan = previous?.plans.find(item => item.id === plan.id);
      const oldOccurrence = oldPlan && cashPlanOccurrence(oldPlan, binding.date);
      if (!oldOccurrence || oldOccurrence.currency !== occurrence.currency || oldOccurrence.direction !== occurrence.direction)
        throw new Error("Belge bağlı planın dövizi/yönü değiştirilemez.");
      continue;
    }
    if (!book) continue;
    changedSources.add(sourceKey);
    const sheet = book?.sheets.find(item => item.id === binding.sourceSheet);
    const row = sheet?.rows.find(item => item.id === binding.sourceRow);
    if (!sheet || !row || sheet.hasMore || book?.errors.some(error => error.id === sheet.id)
      || !["open", "instrument", "loan"].includes(sheet.kind) || !row.date
      || row.currency !== occurrence.currency || row.direction !== occurrence.direction
      || binding.observedAt !== sheet.observedAt || BigInt(row.remaining) < (sourceTotals.get(sourceKey) ?? 0n))
      throw new Error("Belge kapsamı, dövizi veya kalan tutarı doğrulanamadı.");
    if (sheet.kind === "instrument" && !(row.direction === "incoming" ? ["0", "2", "3", "9"] : ["1", "9"]).includes(row.status))
      throw new Error("Çek/senet pozisyonu nakit kapsamında değil.");
  }
  // Shared source allocations are checked together, including unchanged bindings.
  if (book) for (const [key, amount] of sourceTotals) {
    if (!changedSources.has(key)) continue;
    const row = book.sheets.flatMap(sheet => sheet.rows.map(row => ({ sheet: sheet.id, row })))
      .find(item => `${item.sheet}:${item.row.id}` === key)?.row;
    if (row && amount > BigInt(row.remaining)) throw new Error("Aynı belge birden fazla plana fazla bağlandı.");
  }
}
export function cashPlanSheet(data: CashPlans, date: string): WorkbookSheet {
  validateCashPlans(data);
  const end = new Date(`${date}T00:00:00Z`); end.setUTCDate(end.getUTCDate() + 90);
  const rows = data.plans.filter(plan => plan.active).flatMap(plan => cashPlanOccurrences(plan, end.toISOString().slice(0, 10))
    .filter(item => !item.cancelled).map(item => {
    const amounts = cashPlanOccurrenceAmounts(data, plan, item.date);
    return { id: `${plan.id}:${item.date}`, code: item.category, name: item.name, document: "Kullanıcı planı",
      date: item.dueDate, currency: item.currency, amount: item.amount, paid: null,
      remaining: String(amounts.remaining), direction: item.direction,
      status: amounts.bound ? "Tahmin · belgeye bağlı bölüm çıkarıldı · ödeme doğrulanmadı" : "Tahmin · ödeme doğrulanmadı" };
  }));
  if (rows.length > 1000) throw new Error("Plan kayıt sınırı doldu; tarih aralığını daraltın.");
  return { id: "manuel-plan", company: data.company, date, observedAt: data.updatedAt ?? new Date().toISOString(), kind: "plan",
    page: 1, hasMore: false, note: "Sunucuda kayıtlı kullanıcı tahminleri. Belgeye bağlanan tutarlar tekrar toplanmaz; vadesi geçmiş planlar ay hesabının dışındadır.", rows };
}
